/**
 * AddTask2 Component
 * Clean, simple task entry form
 * Uses the new task service with proper validation
 */

import { useState } from 'react';
import { addTask } from '@/services/taskService2';
import toast from 'react-hot-toast';

const INITIAL_FORM = {
  title: '',
  description: '',
  priority: 'medium',
  dueDate: '',       // yyyy-mm-dd
  reminderAt: '',    // yyyy-mm-ddTHH:MM (if you later use datetime-local)
  isFinancialTask: false,
  estimatedAmount: '',
  currency: 'GBP',
};

export default function AddTask2({ listId = 'default', onSuccess, onCancel }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  function handleChange(e) {
    const { name, type, value, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const result = await addTask({
        listId,
        title: form.title,
        description: form.description,
        priority: form.priority,
        dueDate: form.dueDate || null,
        reminderAt: form.reminderAt || null,
        isFinancialTask: form.isFinancialTask,
        estimatedAmount: form.estimatedAmount || null,
        currency: form.currency,
      });

      toast.success('Task added successfully');
      setForm(INITIAL_FORM);
      
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error('Add task error:', err);
      const errorMessage = err.message || 'Failed to add task.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-1">Task title *</label>
        <input
          type="text"
          name="title"
          value={form.title}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full"
          placeholder="e.g. Pay electricity bill"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">
          Description (optional)
        </label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          className="border rounded px-3 py-2 w-full"
          rows={2}
          placeholder="Additional details..."
        />
      </div>

      {/* Priority + Due Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Priority</label>
          <select
            name="priority"
            value={form.priority}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full bg-white"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Due date</label>
          <input
            type="date"
            name="dueDate"
            value={form.dueDate}
            onChange={handleChange}
            className="border rounded px-3 py-2 w-full"
          />
        </div>
      </div>

      {/* Financial task toggle */}
      <div className="flex items-center gap-2">
        <input
          id="isFinancialTask"
          type="checkbox"
          name="isFinancialTask"
          checked={form.isFinancialTask}
          onChange={handleChange}
          className="h-4 w-4"
        />
        <label htmlFor="isFinancialTask" className="text-sm">
          This task is related to money (bill, payment, refund, etc.)
        </label>
      </div>

      {/* Estimated Amount (shown only if financial) */}
      {form.isFinancialTask && (
        <div className="grid grid-cols-3 gap-2 items-end">
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">
              Estimated amount (optional)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              name="estimatedAmount"
              value={form.estimatedAmount}
              onChange={handleChange}
              className="border rounded px-3 py-2 w-full"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Currency</label>
            <input
              type="text"
              name="currency"
              value={form.currency}
              onChange={handleChange}
              className="border rounded px-3 py-2 w-full"
            />
          </div>
        </div>
      )}

      {/* Error feedback */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 px-4 py-2 rounded bg-black text-white hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : 'Add Task'}
        </button>
        
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
